import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  getGlobalProducts,
  searchAllStore,
  getCategory,
  getHotProducts,
  getAuctionList,
  fetchGroup,
} from "../../../utils/services/user.service";
import { addToCart, setCartOpen } from "@/redux/reducers/cartReducer";
import {
  SearchWrap,
  SearchIconWrap,
  SearchInput,
} from "../store/components/StoreDetailNew/styled";

export default function HomeProducts() {
  const history = useHistory();
  const dispatch = useDispatch();
  const userRef = useRef(null);
  const [auctions, setAuctions] = useState([]);
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [combinedItems, setCombinedItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [layout, setLayout] = useState("list");
  const [listCols, setListCols] = useState(2);
  const [showSearch, setShowSearch] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [auctionCount, setAuctionCount] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const itemsPerPage = 50;
  const [search, setSearch] = useState({
    product: "",
    drug: "",
    store: "",
    dosage: "",
    batch: "",
    price: "",
    expire: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    product: "",
    drug: "",
    store: "",
    dosage: "",
    batch: "",
    price: "",
    expire: "",
  });

  // Initialize user ref
  useEffect(() => {
    if (!userRef.current) {
      const userStr = localStorage?.getItem("user");
      if (userStr) {
        userRef.current = JSON.parse(userStr);
      }
    }
  }, []);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getCategory();
        if (response.data.status) {
          setCategories(response.data.category);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch auctions, groups, and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch auctions first (50 records)
        const auctionResponse = await getAuctionList({ limit: 50, page_number: 1 });
        console.log("getAuctionList response:", auctionResponse.data);

        let auctionData = [];
        const auctionCountValue = auctionResponse?.data?.count || 0;
        setAuctionCount(auctionCountValue);

        if (auctionResponse?.data?.data && Array.isArray(auctionResponse.data.data)) {
          auctionData = auctionResponse.data.data.map((auction) => ({
            ...auction,
            isAuction: true,
            _id: auction._id,
            title: auction.product_info?.[0]?.title || "Auction",
            pictures: auction.product_info?.[0]?.pictures || [],
            product_id: auction.product_id,
            auction_start_time: auction.auction_start_time,
            auction_end_time: auction.auction_end_time,
            status: auction.status,
            description: auction.description,
            auction_tier: auction.auction_tier,
            storeName: auction.product_details?.[0]?.store_details?.[0]?.storeName || "Store",
          }));
        }

        console.log("Processed auctions:", auctionData);
        setAuctions(auctionData);

        // Fetch groups
        try {
          const groupResponse = await fetchGroup({ limit: 50, page_number: 1 });
          console.log("fetchGroup response:", groupResponse.data);

          let groupData = [];
          if (groupResponse?.data?.data && Array.isArray(groupResponse.data.data)) {
            groupData = groupResponse.data.data.map((group) => {
              // Use store logo as banner image, fallback to group image/coverImage
              const storeLogo = group.store?.[0]?.storeLogo;
              const pictures = storeLogo 
                ? [storeLogo] 
                : group.image 
                ? [group.image] 
                : group.coverImage 
                ? [group.coverImage] 
                : [];

              return {
                ...group,
                isGroup: true,
                _id: group._id,
                title: group.group_name || "Group Purchase",
                pictures: pictures,
                start_time: group.start_time,
                end_time: group.end_time,
                description: group.description || "Group purchase consortium",
                product_details: group.product_details || [],
                storeName: group.store?.[0]?.storeName || "Group Purchase",
                memberCount: group.invited_users?.length || group.user?.length || group.invited_user?.length || 0,
              };
            });
          }

          console.log("Processed groups:", groupData);
          setGroups(groupData);
        } catch (error) {
          console.error("Error fetching groups:", error);
          setGroups([]);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching auctions:", error);
        setLoading(false);
        setAuctions([]);
      }
    };
    fetchData();
  }, []);

  // Fetch products when appliedFilters or page change; exclude products that are in auction list
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);

        const productResponse = await getGlobalProducts({
          pageNumber: page,
          limit: itemsPerPage,
          product_name: appliedFilters.product || "",
          drug: appliedFilters.drug || "",
          store: appliedFilters.store || "",
          dosage: appliedFilters.dosage || "",
          batch: appliedFilters.batch || "",
          price: appliedFilters.price || "",
          expire: appliedFilters.expire || "",
        });
        console.log("getGlobalProducts response:", productResponse.data);

        let productData = [];
        const productCountValue = productResponse?.data?.count || 0;
        setProductCount(productCountValue);

        if (productResponse?.data?.globalProducts) {
          productData = productResponse.data.globalProducts;
        }

        // Build set of product _ids that appear in auction-list (product_details._id or product_id) - do not show these as regular products
        const auctionProductIds = new Set();
        auctions.forEach((auction) => {
          if (auction.product_id) auctionProductIds.add(String(auction.product_id));
          (auction.product_details || []).forEach((pd) => {
            if (pd._id) auctionProductIds.add(String(pd._id));
          });
        });
        productData = productData.filter((p) => !auctionProductIds.has(String(p._id)));

        if (Array.isArray(productData) && productData.length > 0) {
          const mapped = productData.map((product) => ({
            ...product,
            storeName: product.store?.[0]?.storeName || "Store",
          }));
          setProducts(mapped);
        } else {
          setProducts([]);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setLoading(false);
        setProducts([]);
      }
    };
    fetchProducts();
  }, [appliedFilters, page, itemsPerPage, auctions]);

  // Use productCount from API for pagination (server-side)
  useEffect(() => {
    setTotalCount(productCount);
  }, [productCount]);

  // Combine auctions, groups, and products (auctions first, then groups, then products)
  useEffect(() => {
    const combined = [...auctions, ...groups, ...products];
    setCombinedItems(combined);
  }, [auctions, groups, products]);

  // Filter and sort combined items (auctions + products)
  useEffect(() => {
    console.log("Filtering items. Current items:", combinedItems.length);
    let filtered = [...combinedItems]; // Create a copy to avoid mutating

    // Apply search filters
    // Product name filtering is now done server-side via API
    if (search.category) {
      filtered = filtered.filter((p) => {
        const catName = p.category_name?.[0]?.category_name || p.category_name || "";
        return catName.toLowerCase().includes(search.category.toLowerCase());
      });
    }

    // Sort items
    if (sortKey) {
      filtered = [...filtered].sort((a, b) => {
        const A = a[sortKey];
        const B = b[sortKey];
        if (A < B) return sortDir === "asc" ? -1 : 1;
        if (A > B) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }

    console.log("Filtered items count:", filtered.length);
    setFilteredItems(filtered);
  }, [combinedItems, search, sortKey, sortDir]);

  const handleViewProduct = (item) => {
    if (item.isGroup) {
      // Navigate to group detail page
      const groupId = item._id || item.id;
      history.push(`/groups/${groupId}`);
    } else if (item.isAuction) {
      // Navigate to product details page with product_id
      const productId = item.product_id || item._id;
      history.push(`/productdetails?id=${productId}`);
    } else {
      // Navigate to product detail page
      history.push(
        `/productdetails?title=${encodeURIComponent(item.title)}&id=${encodeURIComponent(item._id)}`
      );
    }
  };

  // Disable Add to cart for product owner (owner cannot add their own product)
  const canAddToCart = (product) => {
    try {
      const user = userRef.current || JSON.parse(localStorage.getItem("user") || "null");
      const currentUserId = user?.data?.user?.user_id || user?.data?.user?._id;
      const productUserId = product?.userId || product?.user_id || product?.current_owner?.[0]?.current_owner_id;
      const productStoreId = product?.store_id;
      const currentStoreId = user?.data?.user?.store_id;
      if (!currentUserId && !currentStoreId) return true;
      if (productUserId && currentUserId && String(productUserId) === String(currentUserId)) return false;
      if (productStoreId && currentStoreId && String(productStoreId) === String(currentStoreId)) return false;
      return true;
    } catch {
      return true;
    }
  };

  const handleAddToCart = (product) => {
    if (!canAddToCart(product)) return;
    const productId = product._id || product.product_id;
    const productName = product.title || product.name || "Product";
    const productImage = product.pictures?.[0] || product.image || product.product_image || product.coverImage || null;
    // Default to 0-index from product_sizes_quantity and save it in Redux
    const productSizesQuantity = product?.product_sizes_quantity;
    const defaultVariant = Array.isArray(productSizesQuantity) && productSizesQuantity.length > 0
      ? productSizesQuantity[0]
      : null;
    const productPrice = defaultVariant?.price ?? product.price ?? 0;
    const availableQty = defaultVariant?.quantity ?? product.quantity ?? 999;

    dispatch(addToCart({
      name: productName + (defaultVariant?.color ? ` (${defaultVariant.color})` : ''),
      baseProductName: productName,
      price: productPrice,
      qty: 1,
      productId: productId,
      variantId: defaultVariant?._id ?? null,
      variantColor: defaultVariant?.color ?? null,
      variant: defaultVariant,
      variants: Array.isArray(productSizesQuantity) ? productSizesQuantity : [],
      image: productImage,
      availableQty: availableQty,
      deliveryCharges: product?.deliveryCharges ?? 0,
      vat: product?.vat ?? 0,
      product_sizes_quantity_0: defaultVariant,
      productSnapshot: {
        seller_wallet: product?.seller_wallet?.address ?? product?.seller_wallet,
        store_id: product?.store_id ?? product?.userId,
        category_id: product?.category_id,
        symbol: product?.symbol,
        chain_id: product?.chain_id,
        approver_wallet_Address: product?.approver_wallet_Address ?? product?.approver_wallet_address,
        buyer_approver_address: product?.buyer_approver_address,
        agency_percentage: product?.agency_percentage,
      },
    }));
    
    dispatch(setCartOpen(true));
    toast.success(`${productName} added to cart!`);
  };

  // Mock categories for images
  const categoryImages = {
    "Real Estate":
      "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=800",
    "Luxury Watch":
      "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=800",
    Art: "https://images.unsplash.com/photo-1504196606672-aef5c9cefc92?auto=format&fit=crop&w=800",
    Jewels:
      "https://images.unsplash.com/photo-1499955085172-a104c9463ece?auto=format&fit=crop&w=800",
    Technology:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800",
    Commodity:
      "https://images.unsplash.com/photo-1581091012184-5c814c53c9f1?auto=format&fit=crop&w=800",
  };

  return (
    <Page>
      <Marketplace
        products={page === 1 ? (filteredItems.length > 0 ? filteredItems : combinedItems) : products}
        layout={layout}
        setLayout={setLayout}
        listCols={listCols}
        setListCols={setListCols}
        showSearch={showSearch}
        setShowSearch={setShowSearch}
        search={search}
        setSearch={setSearch}
        setAppliedFilters={setAppliedFilters}
        sortKey={sortKey}
        setSortKey={setSortKey}
        sortDir={sortDir}
        setSortDir={setSortDir}
        onViewProduct={handleViewProduct}
        onAddToCart={handleAddToCart}
        canAddToCart={canAddToCart}
        categoryImages={categoryImages}
        loading={loading}
        page={page}
        setPage={setPage}
        totalCount={totalCount}
        itemsPerPage={itemsPerPage}
      />
    </Page>
  );
}

function Marketplace({
  products,
  layout,
  setLayout,
  listCols,
  setListCols,
  showSearch,
  setShowSearch,
  search,
  setSearch,
  setAppliedFilters,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
  onViewProduct,
  onAddToCart,
  canAddToCart,
  categoryImages,
  loading,
  page,
  setPage,
  totalCount,
  itemsPerPage,
}) {



  const bottomRef = useRef(null);
  const [bottomOpen, setBottomOpen] = useState(false);
  const [tick, setTick] = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  useEffect(() => {
    if (page > totalPages) {
      setPage(Math.max(1, totalPages));
    }
  }, [totalCount, page, totalPages]);

  useEffect(() => {
    // Update countdown every second
    const t = setInterval(() => setTick((v) => v + 1), 1000);

    const onScroll = () => {
      const nearBottom =
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 200;
      setBottomOpen(nearBottom);
    };

    window.addEventListener("scroll", onScroll);
    return () => {
      clearInterval(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // Format time remaining as DD:HH:MM:SS
  const formatTimeRemaining = (ms) => {
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    return `${String(days).padStart(2, "0")}:${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  return (
    <>
      <Header>
        <HeaderTop>
        <h1>Marketplace</h1>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <SearchWrap>
              <SearchIconWrap aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </SearchIconWrap>
              <SearchInput
                type="text"
                placeholder="Search products..."
                value={search.product}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearch((s) => ({ ...s, product: value }));
                  setAppliedFilters((a) => ({ ...a, product: value }));
                  setPage(1);
                }}
                aria-label="Search products"
              />
            </SearchWrap>
            <LayoutToggle>
              <LayoutBtn
                active={layout === "grid"}
                onClick={() => setLayout("grid")}
              >
                ⬛
              </LayoutBtn>
              <LayoutBtn
                active={layout === "list"}
                onClick={() => setLayout("list")}
              >
                ☰
              </LayoutBtn>
            </LayoutToggle>

            {layout === "list" && (
              <LayoutToggle>
                <LayoutBtn
                  active={listCols === 1}
                  onClick={() => setListCols(1)}
                >
                  1
                </LayoutBtn>
                <LayoutBtn
                  active={listCols === 2}
                  onClick={() => setListCols(2)}
                >
                  2
                </LayoutBtn>
              </LayoutToggle>
            )}
            <SearchBtn onClick={() => setShowSearch(true)} title="Search &amp; Filters">
              🔍
              {(search.drug || search.store || search.dosage || search.batch || search.prize || search.expire) && (
                <ActiveDot />
              )}
            </SearchBtn>
          </div>
        </HeaderTop>
      </Header>

      {showSearch && <DrawerOverlay onClick={() => setShowSearch(false)} />}

      {showSearch && (
        <SearchDrawer>
          <DrawerHeader>
            <span>Search & Filters</span>
            <CloseBtn onClick={() => setShowSearch(false)}>✕</CloseBtn>
          </DrawerHeader>

          <SearchField>
            <label>Product</label>
            <input 
              value={search.product}
              onChange={(e) =>
                setSearch((s) => ({ ...s, product: e.target.value }))
              }
              placeholder="Search products..."
            />
          </SearchField>

          <SearchField>
            <label>Drug</label>
            <input
              placeholder="Search drugs..."
              value={search.drug}
              onChange={(e) =>
                setSearch((s) => ({ ...s, drug: e.target.value }))
              }
            />
          </SearchField>

          <SearchField>
            <label>Store</label>
            <input
              placeholder="Search stores..."
              value={search.store}
              onChange={(e) =>
                setSearch((s) => ({ ...s, store: e.target.value }))
              }
            />
          </SearchField>

          <SearchField>
            <label>Dosage</label>
            <input
              placeholder="Search dosage..."
              value={search.dosage}
              onChange={(e) =>
                setSearch((s) => ({ ...s, dosage: e.target.value }))
              }
            />
          </SearchField>

          <SearchField>
            <label>Batch</label>
            <input
              placeholder="Search batch..."
              value={search.batch}
              onChange={(e) =>
                setSearch((s) => ({ ...s, batch: e.target.value }))
              }
            />
          </SearchField>

          <SearchField>
            <label>Price</label>
            <input
              placeholder="Search Price..."
              value={search.prize}
              onChange={(e) =>
                setSearch((s) => ({ ...s, prize: e.target.value }))
              }
            />
          </SearchField>

          <SearchField>
            <label>Expire</label>
            <input
              type="date"
              value={search.expire}
              onChange={(e) =>
                setSearch((s) => ({ ...s, expire: e.target.value }))
              }
            />
          </SearchField>

          <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
            <ResetBtn
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log("Reset filters clicked");
                setSearch({
                  product: "",
                  drug: "",
                  store: "",
                  dosage: "",
                  batch: "",
                  prize: "",
                  expire: "",
                });
                setAppliedFilters({
                  product: "",
                  drug: "",
                  store: "",
                  dosage: "",
                  batch: "",
                  prize: "",
                  expire: "",
                });
                setPage(1);
              }}
            >
              Reset filter
            </ResetBtn>
            <ApplyBtn
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log("Apply filters clicked:", search);
                setAppliedFilters({
                  product: search.product,
                  drug: search.drug,
                  store: search.store,
                  dosage: search.dosage,
                  batch: search.batch,
                  prize: search.prize,
                  expire: search.expire,
                });
                setPage(1);
                setShowSearch(false);
              }}
            >
              Apply filters
            </ApplyBtn>
          </div>
        </SearchDrawer>
      )}



      <ProductsGrid layout={layout} listCols={listCols}>
        {products.map((p) => {
          const isAuction = p.isAuction || false;
          const isGroup = p.isGroup || false;
          const categoryName = p.category_name?.[0]?.category_name ||
            p.categories?.[0]?.category_name ||
            p.category_name ||
            (isGroup ? "Group Purchase" : "Product");
          // Get product image - check multiple possible image fields
          const productImage = p.pictures?.[0] ||
            p.image ||
            p.product_image ||
            p.coverImage ||
            categoryImages[categoryName] ||
            `https://picsum.photos/400?random=${p._id}`;

          // For auctions, get price from first tier, for products get from product_sizes_quantity, for groups get from product_details
          let price = 0;
          let symbol = "USDT (BEP20)";

          if (isAuction) {
            price = p.auction_tier?.[0]?.price || 0;
          } else if (isGroup) {
            // Get price from first product in product_details
            price = p.product_details?.[0]?.price || 0;
          } else {
            price = p.product_sizes_quantity?.[0]?.price || 0;
            symbol = p.symbol || "USDT (BEP20)";
          }

          // Calculate if auction/group is live (tick is used to force re-render every second for countdown)
          const now = new Date().getTime();
          const endTime = isAuction 
            ? new Date(p.auction_end_time).getTime() 
            : isGroup 
            ? (p.end_time ? new Date(p.end_time).getTime() : 0)
            : 0;
          const remainingMs = Math.max(0, endTime - now);
          const isLive = (isAuction && remainingMs > 0 && p.status === "active") || 
                        (isGroup && remainingMs > 0);
          
          // Check if group has ended
          const isGroupEnded = isGroup && p.end_time && remainingMs <= 0;
          
          // For groups, always show countdown timer if end_time exists (like auction cards)
          const hasEndTime = isGroup && p.end_time;
          const showGroupTimer = hasEndTime && !isGroupEnded; // Don't show timer if ended

          // Format time remaining as DD:HH:MM:SS
          const timeRemaining = (isLive || showGroupTimer) ? formatTimeRemaining(remainingMs) : "00:00:00:00";
          
          return (
          <ProductCard 
              key={p._id}
              layout={layout}
              $active={isLive}
              $group={isGroup}
            >
              {layout === "list" ? (
                <>
                  <ProductImage
                    layout={layout}
                    style={{
                      backgroundImage: `url(${productImage})`,
                    }}
                  />
                  {(isLive || showGroupTimer) && layout === "list" && (
                    isGroup ? <GroupDot /> : <LiveDot />
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "nowrap" }}>
                    <ProductName title={p.title}>{p.title || "Untitled"}</ProductName>
                  </div>
                  <span style={{ opacity: 0.75 }}>{isAuction ? "Auction" : isGroup ? "Group Purchase" : categoryName}</span>
                  <span>{p.storeName || "Store"}</span>
                  {isGroup ? (
                    <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#5ce9ff" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      <span>{p.memberCount || 0} {p.memberCount === 1 ? "member" : "members"}</span>
                    </span>
                  ) : (
                    <span>{isAuction ? ` ${price} ${symbol == "USDT (BEP20)" ? "$" : symbol}` : `${price} ${symbol == "USDT (BEP20)" ? "$" : symbol}`}</span>
                  )}
                  <BtnRow $single={isLive || showGroupTimer || isGroupEnded}>
                    {(isLive || showGroupTimer) ? (
                      <Btn $action $group={isGroup} onClick={() => onViewProduct(p)}>
                        {isGroup ? "Join Group" : "Auction"} · {timeRemaining}
                      </Btn>
                    ) : isGroupEnded ? (
                      <Btn 
                        className="view-btn" 
                        onClick={() => onViewProduct(p)}
                        style={{ 
                          background: 'rgba(107, 114, 128, 0.2)', 
                          borderColor: 'rgba(107, 114, 128, 0.4)',
                          color: '#9ca3af',
                          cursor: 'pointer'
                        }}
                      >
                        View (Ended)
                      </Btn>
                    ) : (
                      <>
                        {!isAuction && !isGroup && (
                          <Btn
                            onClick={() => canAddToCart?.(p) && onAddToCart(p)}
                            disabled={!canAddToCart?.(p)}
                            style={
                              !canAddToCart?.(p)
                                ? { opacity: 0.5, cursor: "not-allowed" }
                                : undefined
                            }
                          >
                            Add
                          </Btn>
                        )}
                        <Btn className="view-btn" onClick={() => onViewProduct(p)}>
                View
              </Btn>
                      </>
                    )}
            </BtnRow>
                </>
              ) : (
                <>
                  <ProductImage
                    layout={layout}
                    style={{
                      backgroundImage: `url(${productImage})`,
                    }}
                  >
                  {(isLive || showGroupTimer) && (
                    isGroup ? <GroupBadge>GROUP</GroupBadge> : <LiveBadge>LIVE</LiveBadge>
                  )}
                  {isGroupEnded && <EndedBadge>ENDED</EndedBadge>}
                  {!(isLive || showGroupTimer) && !isGroupEnded && categoryName && !isGroup && (
                      <CategoryBadge>{categoryName.replace(/_/g, " ")}</CategoryBadge>
                    )}
                    {isGroup && !(isLive || showGroupTimer) && !isGroupEnded && (
                      <GroupBadge>GROUP</GroupBadge>
                    )}
                  </ProductImage>

                  <span>{p.title}</span>
                  <span style={{ opacity: 0.75 }}>
                    {isAuction ? (p.description || "Auction") : isGroup ? (p.description || "Group purchase consortium") : (p.description || "Product description")}
                  </span>
                  <span>{p.storeName || "Store"}</span>

                {isGroup ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#5ce9ff" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    <span>{p.memberCount || 0} {p.memberCount === 1 ? "member" : "members"}</span>
                  </div>
                ) : (
                  <Price>
                      {isAuction ? ` ${price} ${symbol == "USDT (BEP20)" ? "$" : symbol}` : `${price} ${symbol == "USDT (BEP20)" ? "$" : symbol}`}
                  </Price>
                )}
                  <BtnRow $single={isLive || showGroupTimer || isGroupEnded}>
                    {(isLive || showGroupTimer) ? (
                      <Btn $action $group={isGroup} onClick={() => onViewProduct(p)}>
                        {isGroup ? "Join Group" : "Auction"} · {timeRemaining}
                      </Btn>
                    ) : isGroupEnded ? (
                      <Btn 
                        className="view-btn" 
                        onClick={() => onViewProduct(p)}
                        style={{ 
                          background: 'rgba(107, 114, 128, 0.2)', 
                          borderColor: 'rgba(107, 114, 128, 0.4)',
                          color: '#9ca3af',
                          cursor: 'pointer'
                        }}
                      >
                        View (Ended)
                      </Btn>
                    ) : (
                      <>
                        {!isAuction && !isGroup && (
                          <Btn
                            onClick={() => canAddToCart?.(p) && onAddToCart(p)}
                            disabled={!canAddToCart?.(p)}
                            style={
                              !canAddToCart?.(p)
                                ? { opacity: 0.5, cursor: "not-allowed" }
                                : undefined
                            }
                          >
                            Add
                          </Btn>
                        )}
                        <Btn className="view-btn" onClick={() => onViewProduct(p)}>
                View
              </Btn>
                      </>
                    )}
                  </BtnRow>
                </>
              )}
          </ProductCard>
          );
        })}

        {loading && (
          <Skeleton>
            <div></div>
            <div></div>
            <div></div>
          </Skeleton>
        )}

        {products.length === 0 && !loading && (
          <div
            style={{
              gridColumn: "1 / -1",
              textAlign: "center",
              padding: "40px",
              color: "#e5e7eb",
              opacity: 0.7,
            }}
          >
            No Products Available.
          </div>
        )}
      </ProductsGrid>

        <Pagination>
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            {"<"}
          </button>
        {(() => {
          const pagesToShow = [];
          const maxPagesToShow = 5;
          
          if (totalPages <= maxPagesToShow) {
            // Show all pages if total is less than max
            for (let i = 1; i <= totalPages; i++) {
              pagesToShow.push(i);
            }
          } else {
            // Show first page, current page area, and last page
            if (page <= 3) {
              for (let i = 1; i <= 4; i++) {
                pagesToShow.push(i);
              }
              pagesToShow.push('...');
              pagesToShow.push(totalPages);
            } else if (page >= totalPages - 2) {
              pagesToShow.push(1);
              pagesToShow.push('...');
              for (let i = totalPages - 3; i <= totalPages; i++) {
                pagesToShow.push(i);
              }
            } else {
              pagesToShow.push(1);
              pagesToShow.push('...');
              for (let i = page - 1; i <= page + 1; i++) {
                pagesToShow.push(i);
              }
              pagesToShow.push('...');
              pagesToShow.push(totalPages);
            }
          }
          
          return pagesToShow.map((pNum, idx) => {
            if (pNum === '...') {
              return <span key={`ellipsis-${idx}`}>...</span>;
            }
            return (
              <button
                key={pNum}
                className={page === pNum ? "active" : ""}
                onClick={() => setPage(pNum)}
                disabled={loading}
              >
                {pNum}
              </button>
            );
          });
        })()}
        <button onClick={() => setPage((p) => p + 1)} disabled={loading || page >= totalPages}>
            {">"}
          </button>
        </Pagination>

      <BottomBar
        ref={bottomRef}
        open={bottomOpen}
        onMouseEnter={() => setBottomOpen(true)}
        onMouseLeave={() => setBottomOpen(false)}
      >
        <BottomHandle />
        <SuggestionGrid>
          {products.slice(0, 4).map((p) => {
            const categoryName = p.category_name?.[0]?.category_name ||
              p.categories?.[0]?.category_name ||
              p.category_name ||
              "Product";
            const productImage = p.pictures?.[0] || categoryImages[categoryName] || `https://picsum.photos/400?random=${p._id}`;
            const price = p.product_sizes_quantity?.[0]?.price || 0;

            return (
              <SuggestionCard key={p._id} onClick={() => onViewProduct(p)}>
                <SuggestionImage
                  style={{
                    backgroundImage: `url(${productImage})`,
                  }}
                />
                <SuggestionInfo>
                  <strong>{p.title || "Untitled"}</strong>
                  <span>{categoryName}</span>
                  <SuggestionPrice>{price} {p.symbol || "USDT"}</SuggestionPrice>
                </SuggestionInfo>
              </SuggestionCard>
            );
          })}
        </SuggestionGrid>
      </BottomBar>
    </>
  );
}

/* =============================
   Marketplace Custodiy Style
============================= */

const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top, #111827 0, #020617 60%, #000 100%);
  padding: 32px;
  color: #e5e7eb;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif;
`;

const Header = styled.div`
  padding-bottom: 16px;
  margin-bottom: 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  h1 {
    font-size: 26px;
    color: #00f7d2;
    margin-bottom: 12px;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LayoutToggle = styled.div`
  display: flex;
  background: #0d1220;
  border: 1px solid #1f2937;
  border-radius: 10px;
  overflow: hidden;
`;

const LayoutBtn = styled.button`
  padding: 6px 10px;
  background: ${({ active }) => (active ? "#00f7d2" : "transparent")};
  color: ${({ active }) => (active ? "#000" : "#9ca3af")};
  border: none;
  cursor: pointer;
  font-size: 14px;
  transition: 0.2s ease;

  &:hover {
    color: #00f7d2;
  }
`;

const SearchBtn = styled.div`
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(0, 247, 210, 0.15);
  color: #00f7d2;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
  transition: 0.25s ease;

  &:hover {
    background: rgba(0, 247, 210, 0.3);
  }
`;

const ActiveDot = styled.span`
    position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #00f7d2;
  box-shadow: 0 0 8px rgba(0, 247, 210, 0.8);
`;

const TableHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  background: #020617;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 6px;

  & > div {
    display: grid;
    grid-template-columns: 28px 3fr 2fr 1.6fr 1.2fr 1.2fr 1.2fr 1.2fr 1.2fr 96px;
    gap: 6px;
    padding: 6px 8px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    opacity: 0.8;
  }

  span {
    cursor: pointer;
  position: relative;
    user-select: none;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  gap: 12px;

  ${({ layout }) =>
    layout === "grid" &&
    `
      grid-template-columns: repeat(4, 1fr);
      padding: 18px;
      gap: 14px;
      align-items: stretch;

      @media (max-width: 1600px) {
        grid-template-columns: repeat(3, 1fr);
      }
      @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
      }
      @media (max-width: 800px) {
        grid-template-columns: 1fr;
      }
    `}

  ${({ layout, listCols }) =>
    layout === "list" &&
    `
      grid-template-columns: repeat(${listCols}, 1fr);
      @media (max-width: 1200px) {
        grid-template-columns: 1fr;
      }
    `}
`;

const ProductCard = styled.div`
  position: relative;

  display: ${({ layout }) => (layout === "grid" ? "flex" : "grid")};
  flex-direction: ${({ layout }) => (layout === "grid" ? "column" : "row")};

  ${({ layout }) =>
    layout === "grid"
      ? `
    padding: 18px;
    gap: 12px;
    min-height: 300px;
    align-items: stretch;
  `
      : `
    grid-template-columns: 28px 2fr 1.5fr 1.5fr 1fr 220px;
    align-items: center;
    gap: 16px;
    padding: 10px 16px;
    min-height: 60px;
    height: 60px;
    max-height: 60px;
    overflow: hidden;
    
    & > * {
      display: flex;
      align-items: center;
      height: 100%;
    }
    
    & > *:not(:last-child) {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    /* Button container - ensure it takes full width */
    & > *:last-child {
      white-space: nowrap;
      overflow: visible;
      width: 100%;
      min-width: 0;
    }
    
    & > *:last-child button {
      white-space: nowrap;
      overflow: visible;
      text-overflow: clip;
    }
  `}

  /* Highlight GROUP and LIVE items similar to NewCanvas styles */
  background: ${(props) =>
    props.$group
      ? "linear-gradient(90deg, rgba(139,92,246,0.18), rgba(139,92,246,0.05))"
      : props.$active
      ? "linear-gradient(90deg, rgba(0,247,210,0.12), rgba(0,247,210,0.04))"
      : "#0b1020"};
  border: 1px solid ${(props) =>
    props.$group ? "rgba(139,92,246,0.6)" : "#1f2937"};
  border-radius: 12px;
  font-size: 12px;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    background: ${(props) =>
      props.$group ? "rgba(139,92,246,0.12)" : "#0f172a"};
    border-color: ${(props) => (props.$group ? "#8b5cf6" : "#00f7d2")};
    transform: translateY(-2px);
  }
`;

const ProductImage = styled.div`
  position: relative;
  flex-shrink: 0;
  opacity: 0.95;

  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #0d1220;

  /* list view (default) */
  width: 28px;
  height: 28px;
  border-radius: 6px;
  align-self: center;

  /* grid view */
  ${({ layout }) =>
    layout === "grid" &&
    `
    width: 100%;
    height: 140px;
    border-radius: 12px;
  `}
`;

const ProductName = styled.div`
  font-weight: 600;
  font-size: 12.5px;
  color: #f9fafb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Price = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #00f7d2;
  text-shadow: 0 0 8px rgba(0, 247, 210, 0.35);
`;

const LiveBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  background: linear-gradient(180deg, #ff3d3d, #ff7a7a);
  color: #fff;
  box-shadow: 0 0 12px rgba(255, 61, 61, 0.8);
  animation: livePulse 1.2s infinite;
  z-index: 10;

  @keyframes livePulse {
    0% { transform: scale(1); opacity: .9; }
    50% { transform: scale(1.08); opacity: 1; }
    100% { transform: scale(1); opacity: .9; }
  }
`;

const LiveDot = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff3d3d;
  box-shadow: 0 0 8px rgba(255, 61, 61, 0.9);
  animation: livePulseDot 1.2s infinite;
  z-index: 10;
  pointer-events: none;

  @keyframes livePulseDot {
    0% { transform: scale(1); opacity: .9; box-shadow: 0 0 8px rgba(255, 61, 61, 0.9); }
    50% { transform: scale(1.2); opacity: 1; box-shadow: 0 0 12px rgba(255, 61, 61, 1); }
    100% { transform: scale(1); opacity: .9; box-shadow: 0 0 8px rgba(255, 61, 61, 0.9); }
  }
`;

const GroupDot = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #8b5cf6;
  box-shadow: 0 0 8px rgba(139, 92, 246, 0.9);
  animation: groupPulseDot 1.2s infinite;
  z-index: 10;
  pointer-events: none;

  @keyframes groupPulseDot {
    0% { transform: scale(1); opacity: .9; box-shadow: 0 0 8px rgba(139, 92, 246, 0.9); }
    50% { transform: scale(1.2); opacity: 1; box-shadow: 0 0 12px rgba(139, 92, 246, 1); }
    100% { transform: scale(1); opacity: .9; box-shadow: 0 0 8px rgba(139, 92, 246, 0.9); }
  }
`;

const EndedBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  background: linear-gradient(180deg, #6b7280, #4b5563);
  color: #fff;
  box-shadow: 0 0 12px rgba(107, 114, 128, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 10;
`;

const CategoryBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  background: rgba(0, 247, 210, 0.9);
  color: #000;
  box-shadow: 0 0 12px rgba(0, 247, 210, 0.8);
  text-transform: capitalize;
  z-index: 10;
`;

const GroupBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  background: #8b5cf6;
  color: #fff;
  box-shadow: 0 0 12px rgba(139, 92, 246, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 10;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  height: 100%;
  white-space: nowrap;
  width: 100%;
  min-width: 0;

  ${({ $single }) =>
    $single &&
    `
      & > button {
        flex: 1 1 100%;
        width: 100%;
        min-width: 0;
      }
    `}
  
  ${({ $single }) =>
    !$single &&
    `
      & > button {
        flex: 1 1 calc(50% - 3px);
        min-width: 0;
      }
    `}
`;

const Btn = styled.button`
  padding: 6px 10px;
  font-size: 12px;
  border-radius: 10px;
  border: 1px solid rgba(0, 247, 210, 0.6);
  background: linear-gradient(180deg, rgba(0, 247, 210, 0.15), rgba(0, 247, 210, 0.05));
  color: #00f7d2;
  cursor: pointer;
  transition: all 0.25s ease;
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
  height: fit-content;
  min-height: 28px;
  max-height: 32px;
  box-sizing: border-box;
  
  ${({ $action }) => $action && `
    overflow: visible;
    text-overflow: clip;
  `}
  
  ${({ $action }) => !$action && `
    overflow: hidden;
    text-overflow: ellipsis;
  `}

  ${({ $action, $group }) =>
    $action && !$group &&
    `
      background: linear-gradient(180deg, #00f7d2, #5ce9ff);
      color: #000;
      font-weight: 700;
      animation: pulseAction 1.2s infinite;
      box-shadow: 0 0 16px rgba(0,247,210,.6);
    `}
  
  ${({ $action, $group }) =>
    $action && $group &&
    `
      background: linear-gradient(180deg, #8b5cf6, #a78bfa);
      color: #fff;
      font-weight: 700;
      animation: pulseGroupAction 1.2s infinite;
      box-shadow: 0 0 16px rgba(139,92,246,.6);
      border-color: rgba(139,92,246,0.6);
    `}

  &:hover { 
    background: ${({ $group }) => $group 
      ? 'linear-gradient(180deg, #a78bfa, #8b5cf6)' 
      : 'linear-gradient(180deg, #00f7d2, #5ce9ff)'};
    color: ${({ $group }) => $group ? '#fff' : '#000'};
    box-shadow: ${({ $group }) => $group 
      ? '0 0 14px rgba(139, 92, 246, 0.45)' 
      : '0 0 14px rgba(0, 247, 210, 0.45)'};
    transform: translateY(-1px);
  }

  &.view-btn {
    background: linear-gradient(180deg, rgba(92, 233, 255, 0.18), rgba(92, 233, 255, 0.08));
    border-color: rgba(92, 233, 255, 0.6);
    color: #5ce9ff;
    overflow: visible;
    text-overflow: clip;
  }

  &.view-btn:hover {
    background: linear-gradient(180deg, #5ce9ff, #00f7d2);
    color: #000;
    box-shadow: 0 0 16px rgba(92, 233, 255, 0.5);
  }

  @keyframes pulseAction {
    0% { box-shadow: 0 0 6px rgba(0,247,210,.4); }
    50% { box-shadow: 0 0 20px rgba(0,247,210,.9); }
    100% { box-shadow: 0 0 6px rgba(0,247,210,.4); }
  }
  
  @keyframes pulseGroupAction {
    0% { box-shadow: 0 0 6px rgba(139,92,246,.4); }
    50% { box-shadow: 0 0 20px rgba(139,92,246,.9); }
    100% { box-shadow: 0 0 6px rgba(139,92,246,.4); }
  }
`;

const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 9998;
`;

const SearchDrawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 340px;
  height: 100vh;
  background: #020617;
  border-left: 1px solid #1f2937;
  padding: 20px;
  z-index: 9999;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  span {
    font-size: 16px;
    font-weight: 600;
  }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #e5e7eb;
  font-size: 18px;
  cursor: pointer;
`;

const SearchField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;

  label {
    font-size: 11px;
    opacity: 0.6;
  }

  input {
  background: #0d1220;
  border: 1px solid #1f2937;
    border-radius: 8px;
    padding: 6px 8px;
  color: #e5e7eb;
    font-size: 12px;
    outline: none;
  }
`;

const ApplyBtn = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(45deg, #00f7d2, #5ce9ff);
  color: #000;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    box-shadow: 0 0 14px rgba(0, 247, 210, 0.45);
    transform: translateY(-1px);
  }
`;

const ResetBtn = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
    background: transparent;
    color: #e5e7eb;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
`;

const Skeleton = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  padding: 12px 0;

  div {
    height: 160px;
    background: #0d1220;
    border-radius: 12px;
    animation: pulse 1.2s infinite;
  }

  @keyframes pulse {
    0% {
      opacity: 0.3;
    }
    50% {
      opacity: 0.8;
    }
    100% {
      opacity: 0.3;
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 40px;
  gap: 10px;

  button {
    background: #0d1220;
    color: #e5e7eb;
    border: 1px solid #1f2937;
    padding: 8px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: 0.25s ease;
    min-width: 40px;

    &:hover:not(:disabled) {
      border-color: #00f7d2;
      color: #00f7d2;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  span {
    color: #9ca3af;
    padding: 0 4px;
  }

  .active {
    border-color: #00f7d2;
    color: #00f7d2;
    background: rgba(0, 247, 210, 0.1);
  }
`;

/* =============================
   Bottom Suggestion Bar
============================= */

const BottomBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 260px;
  background: linear-gradient(180deg, rgba(2,6,23,.95), rgba(2,6,23,.85));
  border-top: 1px solid rgba(0,247,210,.35);
  transform: translateY(${({ open }) => (open ? "0%" : "85%")});
  transition: transform .35s ease;
  z-index: 9997;
  backdrop-filter: blur(8px);
`;

const BottomHandle = styled.div`
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &::before {
    content: "";
    width: 42px;
    height: 4px;
    border-radius: 999px;
    background: rgba(0,247,210,.7);
    box-shadow: 0 0 10px rgba(0,247,210,.8);
  }
`;

const SuggestionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  padding: 16px 24px 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const SuggestionCard = styled.div`
  background: #0b1020;
  border: 1px solid rgba(0,247,210,.35);
  border-radius: 14px;
  overflow: hidden;
  font-size: 12px;
  cursor: pointer;
  transition: transform .2s ease, box-shadow .2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 18px rgba(0,247,210,.35);
  }
`;

const SuggestionImage = styled.div`
  width: 100%;
  height: 110px;
  background-size: cover;
  background-position: center;
`;

const SuggestionInfo = styled.div`
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    color: #00f7d2;
    display: block;
    font-size: 12px;
    line-height: 1.2;
  }

  span {
    opacity: .7;
    font-size: 11px;
  }
`;

const SuggestionPrice = styled.div`
  margin-top: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #00f7d2;
  text-shadow: 0 0 8px rgba(0,247,210,.35);
`;
